import { Component } from '@theme/component';

/**
 * DAVIDsTEA Sustainability community rail.
 *
 * The rail scrolls on its own: it is an overflow container, so a finger, a trackpad and the
 * keyboard all work with no script. This adds the arrows and drives the progress bar, and
 * nothing else.
 *
 * Both arrows stay out of the document until this runs, and they only come in when the rail
 * overflows: `data-overflow` is set from the rail's own scrollWidth, so a rail wide enough to
 * show everything gets no controls at all rather than a pair that cannot move. Once they are
 * in, they come as a pair and the one at the stop it cannot pass is dimmed rather than pulled.
 * At rest only the right arrow is live, which is the initial state; a right arrow on its own
 * would strand a mouse user at the end of the rail.
 *
 * The step is measured rather than hard-coded: one card plus one gap, read from the first two
 * slides. A card size change in the stylesheet moves the arrows with it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} rail
 * @property {HTMLButtonElement} prev
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} [thumb]
 *
 * @extends {Component<Refs>}
 */
export class DtSustainabilityCommunity extends Component {
  requiredRefs = ['rail', 'prev', 'next'];

  connectedCallback() {
    super.connectedCallback();

    const { rail, prev, next } = this.refs;

    prev.addEventListener('click', this.#onPrev);
    next.addEventListener('click', this.#onNext);
    rail.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#observer = new ResizeObserver(this.#sync);
    this.#observer.observe(rail);

    this.#sync();
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    const { rail, prev, next } = this.refs;
    prev?.removeEventListener('click', this.#onPrev);
    next?.removeEventListener('click', this.#onNext);
    rail?.removeEventListener('scroll', this.#onScroll);
    this.#observer?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #observer;

  /**
   * One card plus one gap. Falls back to the card width alone when a single card is left, and
   * to zero when the rail is empty, which the callers already guard.
   */
  get #step() {
    const items = this.refs.rail.querySelectorAll('.dt-sustainability-community__slide');
    const first = items[0];
    if (!(first instanceof HTMLElement)) return 0;
    const second = items[1];
    if (second instanceof HTMLElement) {
      return second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    }
    return first.getBoundingClientRect().width;
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** @param {number} direction */
  #move(direction) {
    const step = this.#step;
    if (!step) return;
    this.refs.rail.scrollBy({ left: step * direction, behavior: this.#behavior });
  }

  #onPrev = () => this.#move(-1);
  #onNext = () => this.#move(1);
  #onScroll = () => this.#sync();

  /**
   * A single pixel of slack on each end: browsers land on fractional scroll offsets, and an
   * exact comparison leaves an arrow live at a stop it cannot move past.
   */
  #sync = () => {
    const { rail, prev, next, thumb } = this.refs;
    const max = rail.scrollWidth - rail.clientWidth;

    /* The arrows answer to the rail's real overflow, not to a card count: below the slack the
       stylesheet takes them out of the document entirely. The ResizeObserver and the scroll
       listener already bring us back here whenever either number can have moved. */
    this.toggleAttribute('data-overflow', max > 1);
    prev.disabled = rail.scrollLeft <= 1;
    next.disabled = max <= 1 || rail.scrollLeft >= max - 1;

    if (!thumb) return;

    /* The handle is as wide a share of the bar as the viewport is of the rail, and it travels
       the leftover width — so a rail that does not scroll fills the bar rather than emptying
       it. The share is measured rather than fixed: with four cards at 1440 it comes to 44% of
       the 418px bar, and a handle wider than that overstates how much of the rail is on
       screen. */
    const ratio = rail.scrollWidth > 0 ? Math.min(1, rail.clientWidth / rail.scrollWidth) : 1;
    const progress = max > 0 ? Math.min(1, Math.max(0, rail.scrollLeft / max)) : 0;

    thumb.style.setProperty('--dt-lc-thumb-w', `${(ratio * 100).toFixed(3)}%`);
    thumb.style.setProperty(
      '--dt-lc-thumb-x',
      ratio >= 1 ? '0px' : `calc((${(100 / ratio).toFixed(3)}% - 100%) * ${progress.toFixed(4)})`
    );
  };
}

if (!customElements.get('dt-sustainability-community-component')) {
  customElements.define('dt-sustainability-community-component', DtSustainabilityCommunity);
}
